import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublications } from '../contexts/PublicationsContext';

const Publications: React.FC = () => {
  const { getPublishedPublications, getPublicationsByCategory } = usePublications();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const navigate = useNavigate();

  const categories = [
    { id: 'all', name: 'Toutes', icon: '📰' },
    { id: 'actualite', name: 'Actualités', icon: '📢' },
    { id: 'annonce', name: 'Annonces', icon: '📋' },
    { id: 'evenement', name: 'Événements', icon: '🎉' },
    { id: 'formation', name: 'Formation', icon: '🎓' }
  ];

  const publications = selectedCategory === 'all'
    ? getPublishedPublications()
    : getPublicationsByCategory(selectedCategory);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'actualite': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
      case 'annonce': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
      case 'evenement': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200';
      case 'formation': return 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'actualite': return '📢';
      case 'annonce': return '📋';
      case 'evenement': return '🎉';
      case 'formation': return '🎓';
      default: return '📰';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <section id="publications" className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Publications & Actualités
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Restez informés des dernières actualités, annonces et événements de l'ISTMECH
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all duration-200 ${selectedCategory === category.id
                ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400'
                }`}
            >
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* Publications Grid */}
        {publications.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {publications.map((publication) => (
              <div
                key={publication.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700"
              >
                {/* Image placeholder */}
                {publication.imageUrl ? (
                  <img
                    src={publication.imageUrl}
                    alt={publication.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                    <span className="text-6xl text-white opacity-80">
                      {getCategoryIcon(publication.category)}
                    </span>
                  </div>
                )}

                <div className="p-6">
                  {/* Category Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(publication.category)}`}>
                      {getCategoryIcon(publication.category)} {publication.category}
                    </span>
                    <time className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(publication.createdAt)}
                    </time>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
                    {publication.title}
                  </h3>

                  {/* Content */}
                  <div className="text-gray-600 dark:text-gray-300 mb-4">
                    <p className="line-clamp-3">
                      {publication.excerpt}
                    </p>
                  </div>

                  {/* Author */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {publication.author.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {publication.author}
                      </span>
                    </div>

                    {/* Read More Button */}
                    {publication.excerpt && publication.content.length > publication.excerpt.length && (
                      <button
                        onClick={() => navigate(`/publication/${publication.id}`)}
                        className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium transition-all duration-200 hover:scale-105"
                      >
                        <span>Lire plus</span>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Tags */}
                  {publication.tags && publication.tags.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-wrap gap-2">
                        {publication.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📰</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Aucune publication trouvée
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {selectedCategory === 'all'
                ? 'Aucune publication n\'est disponible pour le moment.'
                : `Aucune publication dans la catégorie "${categories.find(c => c.id === selectedCategory)?.name}".`
              }
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Publications;
